<template>
  <section
    v-if="visible && safeTasks.length > 0"
    class="paper-upload-list"
    :class="{ collapsed, dragging: isDragging }"
    :style="panelStyle"
  >
    <header @mousedown="startDrag" @touchstart="startDrag">
      <strong>{{ displayTitle }}</strong>
      <span>{{ displaySummary }}</span>
      <button type="button" @click="$emit('toggle-collapse')">{{ collapsed ? ui.expand : ui.collapse }}</button>
      <button type="button" @click="$emit('close')">{{ ui.close }}</button>
    </header>
    <div v-if="!collapsed" class="paper-upload-task-list">
      <div v-for="task in safeTasks" :key="task.id" class="paper-upload-task">
        <div class="paper-upload-task-head">
          <strong :title="task.fileName">{{ task.fileName }}</strong>
          <span>{{ task.progress }}%</span>
        </div>
        <div class="paper-upload-task-foot">
          <span class="paper-upload-task-status">{{ task.statusText }}</span>
          <button v-if="task.state == 'error'" type="button" @click="$emit('retry-task', task)">{{ ui.retry }}</button>
          <button type="button" @click="$emit('remove-task', task)">{{ task.state == 'running' ? ui.cancel : ui.clear }}</button>
        </div>
        <div class="paper-upload-progress">
          <i :style="{ width: task.progress + '%' }"></i>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getAppUiText } from '@/utils/language/common'

export default {
  name: 'PaperUploadList',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    tasks: {
      type: Array,
      default() {
        return []
      }
    },
    summary: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'us'
    },
    title: {
      type: String,
      default: ''
    },
    bottomOffset: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      dragPosition: null,
      dragState: null,
      previousBodyUserSelect: ''
    }
  },
  computed: {
    ui() {
      return getAppUiText(this.language)
    },
    displayTitle() {
      return this.title || this.ui.uploadList
    },
    displaySummary() {
      const summary = String(this.summary || '')
      const count = summary.match(/\d+\s*\/\s*\d+/)
      return count ? count[0].replace(/\s+/g, '') : ''
    },
    safeTasks() {
      return Array.isArray(this.tasks) ? this.tasks : []
    },
    isDragging() {
      return !!this.dragState
    },
    panelStyle() {
      if (!this.dragPosition) {
        return {
          bottom: typeof this.bottomOffset == 'number' ? `${this.bottomOffset}px` : String(this.bottomOffset || '0px')
        }
      }
      return {
        left: `${this.dragPosition.left}px`,
        top: `${this.dragPosition.top}px`,
        right: 'auto',
        bottom: 'auto'
      }
    }
  },
  watch: {
    collapsed(value, oldValue) {
      this.keepPanelBottomAfterSizeChange(value, oldValue)
    },
    visible(value) {
      if (!value) {
        this.stopDrag()
        return
      }
      this.$nextTick(this.keepPanelInViewport)
    },
    safeTasks(value) {
      if (!value || value.length == 0) {
        this.stopDrag()
        return
      }
      this.$nextTick(this.keepPanelInViewport)
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.keepPanelInViewport)
    }
  },
  beforeDestroy() {
    this.removeDragListeners()
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.keepPanelInViewport)
    }
  },
  methods: {
    startDrag(event) {
      if (event.type == 'mousedown' && event.button !== 0) {
        return
      }
      const target = event.target
      if (target && target.closest && target.closest('button, a, input, textarea, select')) {
        return
      }
      const point = this.getDragPoint(event)
      if (!point || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
        return
      }
      const rect = this.$el.getBoundingClientRect()
      const position = this.clampPanelPosition(rect.left, rect.top, rect.width, rect.height)
      this.dragPosition = position
      this.dragState = {
        offsetX: point.clientX - position.left,
        offsetY: point.clientY - position.top,
        width: rect.width,
        height: rect.height
      }
      this.addDragListeners()
      this.disableTextSelection()
      if (event.cancelable !== false) {
        event.preventDefault()
      }
    },
    handleDragMove(event) {
      if (!this.dragState) {
        return
      }
      const point = this.getDragPoint(event)
      if (!point) {
        return
      }
      const nextLeft = point.clientX - this.dragState.offsetX
      const nextTop = point.clientY - this.dragState.offsetY
      this.dragPosition = this.clampPanelPosition(
        nextLeft,
        nextTop,
        this.dragState.width,
        this.dragState.height
      )
      if (event.cancelable !== false) {
        event.preventDefault()
      }
    },
    stopDrag() {
      if (!this.dragState) {
        return
      }
      this.dragState = null
      this.removeDragListeners()
      this.restoreTextSelection()
      this.$nextTick(this.keepPanelInViewport)
    },
    addDragListeners() {
      if (typeof window === 'undefined') {
        return
      }
      this.removeDragListeners()
      window.addEventListener('mousemove', this.handleDragMove)
      window.addEventListener('mouseup', this.stopDrag)
      window.addEventListener('touchmove', this.handleDragMove, { passive: false })
      window.addEventListener('touchend', this.stopDrag)
      window.addEventListener('touchcancel', this.stopDrag)
    },
    removeDragListeners() {
      if (typeof window === 'undefined') {
        return
      }
      window.removeEventListener('mousemove', this.handleDragMove)
      window.removeEventListener('mouseup', this.stopDrag)
      window.removeEventListener('touchmove', this.handleDragMove)
      window.removeEventListener('touchend', this.stopDrag)
      window.removeEventListener('touchcancel', this.stopDrag)
    },
    getDragPoint(event) {
      if (event.touches && event.touches.length > 0) {
        return event.touches[0]
      }
      if (event.changedTouches && event.changedTouches.length > 0) {
        return event.changedTouches[0]
      }
      if (typeof event.clientX == 'number' && typeof event.clientY == 'number') {
        return event
      }
      return null
    },
    clampPanelPosition(left, top, width, height) {
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : width
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : height
      const margin = 0
      const maxLeft = Math.max(margin, viewportWidth - width - margin)
      const maxTop = Math.max(margin, viewportHeight - height - margin)
      return {
        left: Math.min(Math.max(left, margin), maxLeft),
        top: Math.min(Math.max(top, margin), maxTop)
      }
    },
    keepPanelInViewport() {
      if (!this.dragPosition || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
        return
      }
      const rect = this.$el.getBoundingClientRect()
      this.dragPosition = this.clampPanelPosition(rect.left, rect.top, rect.width, rect.height)
    },
    keepPanelBottomAfterSizeChange(value, oldValue) {
      if (value === oldValue) {
        this.$nextTick(this.keepPanelInViewport)
        return
      }
      if (!this.dragPosition || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
        this.$nextTick(this.keepPanelInViewport)
        return
      }
      const previousRect = this.$el.getBoundingClientRect()
      const previousBottom = previousRect.bottom
      this.$nextTick(() => {
        if (!this.dragPosition || !this.$el || typeof this.$el.getBoundingClientRect != 'function') {
          return
        }
        const rect = this.$el.getBoundingClientRect()
        this.dragPosition = this.clampPanelPosition(
          rect.left,
          previousBottom - rect.height,
          rect.width,
          rect.height
        )
      })
    },
    disableTextSelection() {
      if (typeof document === 'undefined' || !document.body) {
        return
      }
      this.previousBodyUserSelect = document.body.style.userSelect
      document.body.style.userSelect = 'none'
    },
    restoreTextSelection() {
      if (typeof document === 'undefined' || !document.body) {
        return
      }
      document.body.style.userSelect = this.previousBodyUserSelect
    }
  }
}
</script>

<style scoped>
.paper-upload-list {
  position: fixed;
  margin: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: none;
  max-height: 30vh;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #d8d8d8;
  box-shadow: 0 8px 22px rgba(0, 0, 0, .12);
  z-index: 4501;
  display: flex;
  flex-direction: column;
}

.paper-upload-list.collapsed {
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-height: 56px;
}

.paper-upload-list.dragging {
  transition: none;
}

.paper-upload-list.dragging > header {
  cursor: grabbing;
}

.paper-upload-list > header {
  height: 56px;
  padding: 0 18px;
  border-bottom: 1px solid #e5e5e2;
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  flex-shrink: 0;
  cursor: move;
  user-select: none;
  touch-action: none;
}

.paper-upload-list.collapsed > header {
  border-bottom: 0;
}

.paper-upload-list > header strong {
  color: #111;
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  white-space: nowrap;
}

.paper-upload-list > header span {
  min-width: 0;
  color: #666;
  font-size: 14px;
  line-height: 20px;
  flex: 1;
}

.paper-upload-list > header button {
  border: 0;
  background: transparent;
  color: #1f3a2d;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.paper-upload-task-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 16px 14px;
}

.paper-upload-task {
  padding: 12px 4px 14px;
  border-bottom: 1px solid #eeeeeb;
}

.paper-upload-task:last-child {
  border-bottom: 0;
}

.paper-upload-task-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.paper-upload-task-head strong {
  min-width: 0;
  color: #111;
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
}

.paper-upload-task-head span,
.paper-upload-task-status {
  color: #666;
  font-size: 14px;
  line-height: 20px;
}

.paper-upload-task-foot {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 4px;
}

.paper-upload-task-status {
  min-width: 0;
  flex: 1;
}

.paper-upload-task-foot button {
  border: 0;
  background: transparent;
  color: #1f3a2d;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.paper-upload-progress {
  height: 6px;
  margin-top: 10px;
  background: #e4e7e2;
  overflow: hidden;
}

.paper-upload-progress i {
  display: block;
  height: 100%;
  background: #1f3a2d;
  transition: width .18s ease;
}
</style>
